import { useFormikContext } from 'formik';
import { useState } from 'react';
import callApi from '../../utils/callApi';
import { Address } from '../../utils/checkoutState';
import { Country } from '../../utils/countryData';
import { getInitialAddressValues, splitStreetHouseNumber } from '../../utils/streetHouseNumber';
import useQuery from '../../utils/useQuery';
import Modal from '../atoms/Modal';
import AddressList from './AddressList';

type AddressBookAddress = {
  id: number;
  hash: string;
  type: string;
  city: string;
  title?: string;
  firstName: string;
  gender: 'f' | 'm';
  lastName: string;
  streetHouseNumber: string;
  additional?: string;
  zipCode: string;
  state: null;
  countryCode: Country;
};

type AddressBookProps = {
  context: 'shippingAddress' | 'billingAddress';
  onClose: () => void;
};

const formatAddressBook = (addressBook: AddressBookAddress[]): Address[] =>
  addressBook.map(address => {
    const { id, hash, title, countryCode, gender, firstName, lastName, streetHouseNumber, additional, zipCode, city } =
      address;
    const { street, houseNumber } = splitStreetHouseNumber(streetHouseNumber, countryCode);

    return {
      id,
      hash,
      title,
      country: { iso2Code: countryCode },
      salutationCode: gender,
      firstName,
      lastName,
      street,
      houseNumber,
      additional,
      zipCode,
      city,
    };
  });
const AddressBook = ({ context, onClose }: AddressBookProps) => {
  const [selectedAddressId, setSelectedAddressId] = useState<number>();
  const { values, setFieldValue, setFieldTouched, validateForm } = useFormikContext<any>();
  const { data: rawAddressBook, loading, setData } = useQuery<AddressBookAddress[]>('/address', 'legacy');
  const addressBook = rawAddressBook && formatAddressBook(rawAddressBook);

  const onDelete = async (addressId: number) => {
    if (addressId === selectedAddressId) {
      setSelectedAddressId(undefined);
    }
    setData(rawAddressBook?.filter(address => address.id !== addressId));
    await callApi({ method: 'DELETE', endpoint: `/address/${addressId}`, flow: 'legacy' });
  };

  return (
    <Modal>
      <AddressList
        addresses={addressBook}
        isLoading={loading}
        context={context}
        onDelete={onDelete}
        usage="stored"
        onCancel={onClose}
        onSelect={address => {
          onClose();
          const newValues = getInitialAddressValues(address);
          setFieldValue(context, { ...values[context], ...newValues });
          setTimeout(() => {
            Object.keys(newValues).forEach(key => setFieldTouched(`${context}.${key}`));
            validateForm();
          }, 0);
        }}
      />
    </Modal>
  );
};

export default AddressBook;
